export default class ContactForm {
  constructor() {
    this.contactForm = document.querySelector('.js-contact_form');

    if (this.contactForm) {
      this.createStatusMessageRegion();
      this.initializeValidation();
      this.setupCustomValidationMessages();
      this.contactForm.addEventListener('submit', (event) => {
        event.preventDefault();
        if (this.validateForm()) {
          ContactForm.submit();
        }
      });
    }
  }

  createStatusMessageRegion() {
    // Create status message region compliant with WCAG 2.2 SC 4.1.3
    this.statusRegion = document.createElement('div');
    this.statusRegion.setAttribute('role', 'status');
    this.statusRegion.setAttribute('aria-live', 'polite');
    this.statusRegion.setAttribute('aria-atomic', 'true');
    this.statusRegion.className = 'sr-only';
    this.statusRegion.style.cssText = 'position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden;';
    this.contactForm.appendChild(this.statusRegion);
  }

  announceStatusMessage(message) {
    this.statusRegion.textContent = message;
  }

  setupCustomValidationMessages() {
    // Set custom validation messages for each field with more specific, accessible messages
    const customMessages = {
      'fromName': 'Please enter your full name',
      'fromEmail': 'Please enter a valid email address',
      'message[Phone Number]': 'Please enter your phone number',
      'message[Business Name]': 'Please enter your business name',
      'message[Street Address]': 'Please enter your street address',
      'message[City]': 'Please enter your city or town',
      'message[Zip Code]': 'Please enter your 5-digit zip code',
      'message[Estimated Coffee Volume & Frequency]': 'Please describe how much coffee you need and how often (example: 20 pounds per week)',
      'message[Equipment]': 'Please describe your current equipment or what you need to purchase',
      'message[How did you hear about Coava?]': 'Please tell us how you discovered Coava Coffee',
      'message[body]': 'Please describe your business and why you would like to partner with Coava'
    };

    // Apply custom messages to form fields
    Object.entries(customMessages).forEach(([fieldName, message]) => {
      const field = this.contactForm.querySelector(`[name="${fieldName}"]`);
      if (field) {
        field.setAttribute('data-custom-validation-message', message);

        // Override the default validation message
        field.addEventListener('invalid', (event) => {
          event.preventDefault();
          if (field.validity.valueMissing) {
            field.setCustomValidity(message);
          } else {
            field.setCustomValidity('');
          }
        });

        // Clear custom validation message when user starts typing
        field.addEventListener('input', () => {
          field.setCustomValidity('');
        });
      }
    });
  }

  initializeValidation() {
    // Define validation rules
    this.validationRules = {
      'fromName': { required: true, minlength: 2 },
      'fromEmail': { required: true, email: true },
      'message[Phone Number]': { required: true, phone: true },
      'message[Business Name]': { required: true, minlength: 2 },
      'message[Street Address]': { required: true, minlength: 5 },
      'message[City]': { required: true, minlength: 2 },
      'message[Zip Code]': { required: true, zipcode: true },
      'message[Estimated Coffee Volume & Frequency]': { required: true, minlength: 5 },
      'message[Equipment]': { required: true, minlength: 1 },
      'message[How did you hear about Coava?]': { required: true, minlength: 1 },
      'message[body]': { required: true, minlength: 1 },
      'message[Topic]': { required: true }
    };

    // Define custom error messages with more specific, accessible guidance
    this.errorMessages = {
      'fromName': {
        required: 'Please enter your full name',
        minlength: 'Name must be at least 2 characters long'
      },
      'fromEmail': {
        required: 'Please enter your email address',
        email: 'Please enter a valid email address (example: yourname@company.com)'
      },
      'message[Phone Number]': {
        required: 'Please enter your phone number',
        phone: 'Please enter a valid phone number (example: 555-123-4567)'
      },
      'message[Business Name]': {
        required: 'Please enter your business or organization name',
        minlength: 'Business name must be at least 2 characters long'
      },
      'message[Street Address]': {
        required: 'Please enter your complete street address',
        minlength: 'Address must be at least 5 characters long'
      },
      'message[City]': {
        required: 'Please enter your city or town',
        minlength: 'City must be at least 2 characters long'
      },
      'message[Zip Code]': {
        required: 'Please enter your zip code',
        zipcode: 'Please enter a valid 5-digit zip code'
      },
      'message[Estimated Coffee Volume & Frequency]': {
        required: 'Please describe how much coffee you need and how often',
        minlength: 'Please provide more details about your coffee volume and frequency needs'
      },
      'message[Equipment]': {
        required: 'Please describe your current equipment or what you need to purchase',
        minlength: 'Please provide more details about your equipment needs'
      },
      'message[How did you hear about Coava?]': {
        required: 'Please tell us how you discovered Coava Coffee',
        minlength: 'Please provide more details about how you heard about us'
      },
      'message[body]': {
        required: 'Please describe your business and why you would like to partner with Coava',
        minlength: 'Please provide more details about your business and partnership goals'
      },
      'message[Topic]': {
        required: 'Please select a topic from the dropdown menu'
      }
    };

    // Add real-time validation listeners
    this.addValidationListeners();
  }

  addValidationListeners() {
    const inputs = this.contactForm.querySelectorAll('input, textarea, select');

    inputs.forEach(input => {
      // Validate on blur
      input.addEventListener('blur', () => {
        this.validateField(input);
      });

      // Clear errors on input
      input.addEventListener('input', () => {
        this.clearFieldError(input);
      });

      // Validate on change (for selects)
      input.addEventListener('change', () => {
        this.validateField(input);
      });
    });
  }

  validateForm() {
    let isValid = true;
    const inputs = this.contactForm.querySelectorAll('input, textarea, select');
    const errorCount = 0;

    inputs.forEach(input => {
      if (!this.validateField(input)) {
        isValid = false;
      }
    });

    // Announce validation status
    if (!isValid) {
      const errorFields = this.contactForm.querySelectorAll('label.error');
      const errorCount = errorFields.length;
      this.announceStatusMessage(`Form validation failed. ${errorCount} field${errorCount > 1 ? 's have' : ' has'} errors.`);
    }

    return isValid;
  }

  validateField(field) {
    const fieldName = field.name;
    const rules = this.validationRules[fieldName];

    if (!rules) return true;

    const value = field.value.trim();
    let isValid = true;
    let errorMessage = '';

    // Check each rule
    for (const [rule, ruleValue] of Object.entries(rules)) {
      if (!this.checkRule(value, rule, ruleValue)) {
        isValid = false;
        errorMessage = this.getErrorMessage(fieldName, rule);
        break;
      }
    }

    if (!isValid) {
      this.showFieldError(field, errorMessage);
      // Announce field-specific error
      const fieldLabel = this.getFieldLabel(field);
      this.announceStatusMessage(`${fieldLabel}: ${errorMessage}`);
    } else {
      this.clearFieldError(field);
    }

    return isValid;
  }

  getFieldLabel(field) {
    // Try to get label from associated label element
    const id = field.getAttribute('id');
    if (id) {
      const label = document.querySelector(`label[for="${id}"]`);
      if (label) {
        return label.textContent.trim();
      }
    }

    // Try to get label from parent label element
    const parentLabel = field.closest('label');
    if (parentLabel) {
      return parentLabel.textContent.trim();
    }

    // Fallback to field name
    return field.name.replace(/[\[\]]/g, '').replace(/([A-Z])/g, ' $1').trim();
  }

  checkRule(value, rule, ruleValue) {
    switch (rule) {
      case 'required':
        return value.length > 0;

      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value);

      case 'phone':
        const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
        const cleanPhone = value.replace(/[\s\-\(\)]/g, '');
        return phoneRegex.test(cleanPhone);

      case 'zipcode':
        const zipRegex = /^\d{5}(-\d{4})?$/;
        return zipRegex.test(value);

      case 'minlength':
        return value.length >= ruleValue;

      case 'maxlength':
        return value.length <= ruleValue;

      default:
        return true;
    }
  }

  getErrorMessage(fieldName, rule) {
    const fieldMessages = this.errorMessages[fieldName];
    return fieldMessages ? fieldMessages[rule] : 'This field is invalid';
  }

  showFieldError(field, message) {
    this.clearFieldError(field);

    const errorLabel = document.createElement('label');
    errorLabel.className = 'error';
    errorLabel.textContent = message;

    field.parentNode.insertBefore(errorLabel, field.nextSibling);
    field.classList.add('error');
  }

  clearFieldError(field) {
    const existingError = field.parentNode.querySelector('label.error');
    if (existingError) {
      existingError.remove();
    }
    field.classList.remove('error');
  }

  static submit() {
    const form = document.querySelector('.js-contact_form');
    const errorLabels = form.querySelectorAll('label.error');
    errorLabels.forEach(label => label.remove());

    const formData = new FormData(form);

    // Announce form submission
    const statusRegion = form.querySelector('[role="status"]');
    if (statusRegion) {
      statusRegion.textContent = 'Submitting form...';
    }

    fetch('/actions/contactForm/sendMessage', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: new URLSearchParams(formData).toString()
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return response.json();
    })
    .then(response => {
      if (response.success) {
        const submitBtn = form.querySelector('.js-form_submit');
        const successMsg = form.querySelector('.js-form_success');

        if (submitBtn) submitBtn.style.display = 'none';
        if (successMsg) successMsg.style.display = 'block';

        form.querySelectorAll('input, textarea, select').forEach(input => {
          input.disabled = true;
        });

        // Announce success
        if (statusRegion) {
          statusRegion.textContent = 'Form submitted successfully. Thank you for your message.';
        }
        return;
      }

      if (response.error) {
        if (response.error === 'Unable to validate reCaptcha.') {
          alert('Unable to validate captcha. Please try again.');
          if (statusRegion) {
            statusRegion.textContent = 'Captcha validation failed. Please try again.';
          }
        } else {
          Object.entries(response.error).forEach(([fieldName, errors]) => {
            const field = form.querySelector(`[name="${fieldName}"]`);
            if (field) {
              const errorLabel = document.createElement('label');
              errorLabel.className = 'error';
              errorLabel.textContent = errors[0];
              field.parentNode.insertBefore(errorLabel, field.nextSibling);
            }
          });

          // Announce server-side errors
          if (statusRegion) {
            const errorCount = Object.keys(response.error).length;
            statusRegion.textContent = `Form submission failed. ${errorCount} field${errorCount > 1 ? 's have' : ' has'} errors.`;
          }
        }
      }
    })
    .catch(error => {
      console.error('Form submission error:', error);
      alert('There was an error submitting the form. Please try again.');

      // Announce error
      if (statusRegion) {
        statusRegion.textContent = 'There was an error submitting the form. Please try again.';
      }
    });
  }
}
